import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

interface AccountInfo {
  id: string
  tier: 'free' | 'starter' | 'pro' | 'growth' | 'scale'
  polar_subscription_status: string | null
  billing_interval: string | null
  emails_sent_this_period: number
  period_start: string | null
  period_end: string | null
  overage_strikes: number
  overage_acknowledged: boolean
  onboarding_completed: boolean
  onboarding_steps: Record<string, boolean> | null
  sending_paused: boolean
  pause_reason: string | null
  suspension_level: number
  suspension_count: number
  suspension_expires_at: string | null
  appeal_status: string
  appeal_submitted_at: string | null
  throttle_percent: number | null
  marketing_tier: string | null
  marketing_emails_sent_this_period: number
  marketing_period_start: string | null
  marketing_period_end: string | null
  contact_count: number
  created_at: string
  updated_at: string
}

interface UserInfo {
  id: string
  email: string
  name: string | null
}

interface AccountResponse {
  account: AccountInfo
  user: UserInfo
}

const QUERY_KEY = ['account'] as const

export function useAccount(): ReturnType<typeof useQuery<AccountResponse>> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get<AccountResponse>(endpoints.account.get),
  })
}

export function useDeleteAccount(): ReturnType<typeof useMutation<unknown, unknown, void>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.delete(endpoints.account.get),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export type { AccountInfo, AccountResponse, UserInfo }
